import { getImpactScore } from '@src/utility/impactScore';
import { memo } from 'react';
import styled from 'styled-components';

import ImpactScoreBadge from '../../sidepanel/ImpactScoreBadge';

import type { ShareableImageType } from '../../../types.ts/story';

type Props = {
  quote: ShareableImageType;
  isSelected: boolean;
  onSelect: (id: string) => void;
};

const PullQuoteSelectionCard = memo(
  ({ quote, isSelected, onSelect }: Props) => {
    const impactScore = getImpactScore(quote.metadata);
    const length = quote.quote?.length ?? 0;
    const sizeTier = length > 260 ? 'sm' : length > 180 ? 'md' : 'lg';

    return (
      <Card onClick={() => onSelect(quote.id)} $isSelected={isSelected}>
        <CardHeader>
          <HeaderLeft>
            {impactScore != null && Object.keys(impactScore).length > 0 && (
              <ImpactScoreBadge
                impactScore={impactScore}
                id={`quote-select-${quote.id}`}
              />
            )}
          </HeaderLeft>
          <SelectButton active={isSelected}>
            {isSelected ? '✓' : ''}
          </SelectButton>
        </CardHeader>
        <CardBody>
          <QuoteText $sizeTier={sizeTier}>{quote.quote}</QuoteText>
        </CardBody>
        {quote.storytellerName && (
          <CardFooter>
            <SpeakerName>— {quote.storytellerName}</SpeakerName>
          </CardFooter>
        )}
      </Card>
    );
  },
);

PullQuoteSelectionCard.displayName = 'PullQuoteSelectionCard';

export default PullQuoteSelectionCard;

const Card = styled.div<{ $isSelected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background-color: var(--surface);
  box-sizing: border-box;
  cursor: pointer;
  height: 220px;
  transition:
    box-shadow var(--transition-normal) ease,
    border-color var(--transition-fast) ease;
  border: 1.5px solid
    ${props => (props.$isSelected ? 'var(--primary)' : 'var(--border)')};
  overflow: hidden;

  &:hover {
    box-shadow: var(--elevation-sm);
    border-color: ${props =>
      props.$isSelected ? 'var(--primary)' : 'var(--text-muted)'};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 0;
  min-height: 28px;
`;

const SelectButton = styled.div.attrs((props: { active: boolean }) => props)`
  cursor: pointer;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-left: auto;
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 1.5px solid var(--text-muted);
  color: var(--text-muted);
  transition: all var(--transition-fast) ease;
  flex-shrink: 0;
  ${props =>
    props.active &&
    `
      background-color: var(--primary);
      border-color: var(--primary);
      color: var(--background);
    `}
`;

const CardBody = styled.div`
  padding: 8px 20px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const QuoteText = styled.p<{ $sizeTier: 'sm' | 'md' | 'lg' }>`
  margin: 0;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-weight: var(--font-weight-normal);
  line-height: 1.6;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  ${props => {
    switch (props.$sizeTier) {
      case 'sm':
        return `font-size: 11px; -webkit-line-clamp: 7;`;
      case 'md':
        return `font-size: 12px; -webkit-line-clamp: 6;`;
      default:
        return `font-size: 13px; -webkit-line-clamp: 5;`;
    }
  }}
`;

const CardFooter = styled.div`
  padding: 0 20px 16px;
`;

const SpeakerName = styled.span`
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
`;
